import type { ColumnDef } from '@tanstack/react-table'
import { Badge } from '@/components/ui/badge'
import { DataTableColumnHeader } from '@/components/data-table'
import { formatDateTime } from '@/lib/format'

export type AdminAnnouncementRow = {
  id: string
  title: string
  body: string
  severity: 'info' | 'warning' | 'critical'
  cta_url: string | null
  cta_label: string | null
  locale: string
  starts_at: string
  ends_at: string | null
  created_at: string
  status: 'scheduled' | 'active' | 'ended'
}

export const ADMIN_ANNOUNCEMENT_SORT_IDS = [
  'startsAt',
  'endsAt',
  'createdAt',
  'severity',
  'status',
  'title',
] as const

export const ADMIN_ANNOUNCEMENT_FILTER_KEYS = [
  'status',
  'severity',
  'locale',
] as const

const STATUS_OPTIONS = [
  { label: 'All', value: 'all' },
  { label: 'Active', value: 'active' },
  { label: 'Scheduled', value: 'scheduled' },
  { label: 'Ended', value: 'ended' },
] as const

const SEVERITY_OPTIONS = [
  { label: 'Info', value: 'info' },
  { label: 'Warning', value: 'warning' },
  { label: 'Critical', value: 'critical' },
] as const

const LOCALE_OPTIONS = [
  { label: 'English', value: 'en' },
  { label: 'Spanish', value: 'es' },
] as const

function statusBadge(status: AdminAnnouncementRow['status']) {
  const variant =
    status === 'active'
      ? 'default'
      : status === 'ended'
        ? 'secondary'
        : 'outline'
  return (
    <Badge variant={variant} className="font-normal capitalize">
      {status}
    </Badge>
  )
}

export function createAdminAnnouncementsColumns(): ColumnDef<
  AdminAnnouncementRow,
  unknown
>[] {
  return [
    {
      id: 'title',
      accessorKey: 'title',
      meta: { label: 'Title' },
      enableSorting: true,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Title" />
      ),
      cell: ({ row }) => (
        <div className="min-w-0 max-w-[16rem]">
          <div className="truncate text-sm font-medium">
            {row.original.title}
          </div>
          <div className="truncate text-xs text-muted-foreground">
            {row.original.body.slice(0, 80)}
            {row.original.body.length > 80 ? '…' : ''}
          </div>
        </div>
      ),
    },
    {
      id: 'status',
      accessorKey: 'status',
      meta: {
        label: 'Status',
        variant: 'select',
        options: [...STATUS_OPTIONS],
      },
      enableSorting: true,
      enableColumnFilter: true,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Status" />
      ),
      cell: ({ row }) => statusBadge(row.original.status),
    },
    {
      id: 'severity',
      accessorKey: 'severity',
      meta: {
        label: 'Severity',
        variant: 'select',
        options: [...SEVERITY_OPTIONS],
      },
      enableSorting: true,
      enableColumnFilter: true,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Severity" />
      ),
      cell: ({ row }) => (
        <span className="text-sm capitalize">{row.original.severity}</span>
      ),
    },
    {
      id: 'locale',
      accessorKey: 'locale',
      meta: {
        label: 'Locale',
        variant: 'select',
        options: [...LOCALE_OPTIONS],
      },
      enableSorting: false,
      enableColumnFilter: true,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Locale" />
      ),
      cell: ({ row }) => (
        <span className="text-xs uppercase">{row.original.locale}</span>
      ),
    },
    {
      id: 'startsAt',
      accessorKey: 'starts_at',
      meta: { label: 'Starts' },
      enableSorting: true,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Starts" />
      ),
      cell: ({ row }) => (
        <span className="whitespace-nowrap text-xs text-muted-foreground">
          {formatDateTime(row.original.starts_at)}
        </span>
      ),
    },
    {
      id: 'endsAt',
      accessorKey: 'ends_at',
      meta: { label: 'Ends' },
      enableSorting: true,
      enableHiding: true,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Ends" />
      ),
      cell: ({ row }) => (
        <span className="whitespace-nowrap text-xs text-muted-foreground">
          {row.original.ends_at ? formatDateTime(row.original.ends_at) : 'Open'}
        </span>
      ),
    },
  ]
}
